// CR159: Merchant Sources Diagnostics Component
// Specialized UI for NO_MERCHANT_SOURCES errors with actionable guidance
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { 
  AlertTriangle, 
  ChevronDown, 
  ChevronRight, 
  Copy, 
  CheckCircle2, 
  ExternalLink,
  Store,
  XCircle,
  AlertCircle,
  Link as LinkIcon
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface MerchantSourcesDiagnosticsProps {
  debug: {
    error_code?: string;
    merchant_ids?: string[];
    scoped_merchant_keys?: string[];
    source_type_requested?: string;
    source_type_aliases_checked?: string[];
    filters_applied?: Record<string, unknown>;
    per_merchant_source_counts?: Record<string, number>;
    filtered_out_reasons?: Record<string, number>;
    suggested_fix?: string;
    all_sources_found?: number;
    schedule_run_id?: string | null;
    timestamp?: string;
  };
  stepId?: string;
}

export function MerchantSourcesDiagnostics({ debug, stepId }: MerchantSourcesDiagnosticsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  const merchantIds = debug.merchant_ids || debug.scoped_merchant_keys || [];
  const perMerchantCounts = debug.per_merchant_source_counts || {};
  const filteredOutReasons = debug.filtered_out_reasons || {};
  const suggestedFix = debug.suggested_fix;
  const allSourcesFound = debug.all_sources_found ?? 0;
  const sourceTypeRequested = debug.source_type_requested || "WEB";

  // Count merchants with zero sources
  const merchantsWithZero = merchantIds.filter(id => (perMerchantCounts[id] ?? 0) === 0);
  const merchantsWithSources = merchantIds.filter(id => (perMerchantCounts[id] ?? 0) > 0);

  // Get top filtered reasons
  const topReasons = Object.entries(filteredOutReasons)
    .filter(([_, count]) => count > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const handleCopyDiagnostics = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({
        error_code: debug.error_code,
        step_id: stepId,
        ...debug,
      }, null, 2));
      setCopied(true);
      toast({ title: "Copied", description: "Diagnostics copied to clipboard" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Failed to copy", variant: "destructive" });
    }
  };

  const handleOpenMerchantSources = () => {
    // Navigate to merchant sources with filter if possible
    navigate("/admin/merchant-sources");
  };

  const getReasonLabel = (reason: string): string => {
    const labels: Record<string, string> = {
      disabled: "Disabled sources",
      wrong_source_type: "Wrong source type",
      no_endpoint: "Missing URL",
      no_sources_at_all: "No sources configured",
      low_priority_skipped: "Lower priority skipped",
    };
    return labels[reason] || reason;
  };

  const getReasonIcon = (reason: string) => {
    if (reason === "no_sources_at_all") return <XCircle className="h-3 w-3 text-destructive" />;
    if (reason === "disabled") return <AlertCircle className="h-3 w-3 text-warning" />;
    if (reason === "wrong_source_type") return <AlertTriangle className="h-3 w-3 text-warning" />;
    return <AlertCircle className="h-3 w-3 text-muted-foreground" />;
  };

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-base">No Merchant Sources Found</CardTitle>
          </div>
          <Badge variant="destructive">
            {merchantsWithZero.length}/{merchantIds.length} merchants missing
          </Badge>
        </div>
        <CardDescription className="text-destructive/80">
          Website Collect (S2) could not find any active sources to process
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-background rounded-md p-2 text-center">
            <div className="text-2xl font-bold text-destructive">{merchantsWithZero.length}</div>
            <div className="text-xs text-muted-foreground">Merchants with 0 sources</div>
          </div>
          <div className="bg-background rounded-md p-2 text-center">
            <div className="text-2xl font-bold text-muted-foreground">{allSourcesFound}</div>
            <div className="text-xs text-muted-foreground">Total sources found</div>
          </div>
          <div className="bg-background rounded-md p-2 text-center">
            <div className="text-2xl font-bold">{sourceTypeRequested}</div>
            <div className="text-xs text-muted-foreground">Source type requested</div>
          </div>
        </div>

        {suggestedFix && (
          <div className="bg-warning/10 border border-warning/30 rounded-md p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-warning mt-0.5 shrink-0" />
              <div className="text-sm">
                <span className="font-medium">Suggested Fix: </span>
                <span>{suggestedFix}</span>
              </div>
            </div>
          </div>
        )}

        {/* Top Filtered Reasons */}
        {topReasons.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Why sources were filtered out
            </div>
            <div className="flex flex-wrap gap-2">
              {topReasons.map(([reason, count]) => (
                <Badge key={reason} variant="outline" className="gap-1.5">
                  {getReasonIcon(reason)}
                  {getReasonLabel(reason)}: {count}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Per-Merchant Breakdown */}
        <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1 h-7 px-2 text-xs w-full justify-start">
              {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              Per-Merchant Breakdown ({merchantIds.length} merchants)
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 max-h-48 overflow-y-auto space-y-1 bg-background rounded-md p-2 border">
              {merchantIds.map(merchantId => {
                const sourceCount = perMerchantCounts[merchantId] ?? 0;
                const hasZero = sourceCount === 0;
                return (
                  <div 
                    key={merchantId} 
                    className={`flex items-center justify-between py-1 px-2 rounded text-sm ${
                      hasZero ? "bg-destructive/10" : "bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Store className={`h-3.5 w-3.5 ${hasZero ? "text-destructive" : "text-green-500"}`} />
                      <span className={hasZero ? "text-destructive font-medium" : ""}>{merchantId}</span>
                    </div>
                    <Badge variant={hasZero ? "destructive" : "secondary"} className="text-xs">
                      {sourceCount} {sourceCount === 1 ? "source" : "sources"}
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t">
          <Button 
            variant="default" 
            size="sm" 
            className="gap-1.5"
            onClick={handleOpenMerchantSources}
          >
            <LinkIcon className="h-3.5 w-3.5" />
            Open Merchant Sources
            <ExternalLink className="h-3 w-3" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-1.5"
            onClick={handleCopyDiagnostics}
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy Diagnostics
              </>
            )}
          </Button>
        </div>

        {/* Source Type Aliases (collapsed by default) */}
        {debug.source_type_aliases_checked && debug.source_type_aliases_checked.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Valid source types for "{sourceTypeRequested}" ({debug.source_type_aliases_checked.length} aliases)
            </summary>
            <div className="mt-1 p-2 bg-muted/50 rounded text-xs font-mono">
              {debug.source_type_aliases_checked.join(", ")}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
